import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { PROFILE_DTO } from "../enums/ApiPaths";
import { fetch } from "../enums/queryFunction/Fetch";
import * as urlPaths from "../enums/UrlPaths";
import { LOGGED_IN_USER } from "../enums/QueryKeys";
import * as roleEnums from "../enums/Roles";
import {
  getGoogleId,
  getNeedToChangePassword,
  getSubsribesToEmail,
  getToken,
  isPasswordEmpty,
  logout,
} from "../utils/Functions";
import React from "react";

export function parseTokenLoginData(data) {
  return {
    token: data?.token,
    refreshToken: data?.refreshToken,
  };
}

export function parseLoginData(data) {
  return {
    googleId: data?.googleId,
    needToChangePassword: data?.needToChangePassword,
    needToAddPhoneNumber: !data?.phoneNumber || data?.phoneNumber.trim() === "",
  };
}

function useFetchUser() {
  const PUBLIC_PATHS = [
    urlPaths.LOGIN,
    urlPaths.SIGN_UP,
    urlPaths.TERMS,
    urlPaths.VERIFY,
    urlPaths.FORGOTTEN_PASSWORD,
    urlPaths.CHANGE_FORGOTTEN_PASSWORD,
    urlPaths.RESEND_VERIFICATION_EMAIL,
    urlPaths.EMPLOYEE_ACTIVATION,
    urlPaths.USER_ACTIVATION,
    urlPaths.USER_ACTIVATION_CONFIRM,
    urlPaths.NEW_EMAIL_CONFIRMATION,
    urlPaths.CHANGE_USER_EMAIL,
    urlPaths.QR_REDIRECT,
    urlPaths.ACCOUNT_DELETE_REQUEST_PAGE,
    urlPaths.ACCOUNT_DELETE_CONFIRMATION_PAGE,
  ];
  const navigate = useNavigate();
  const location = useLocation();
  const isPublicPath = PUBLIC_PATHS.some((p) =>
    location.pathname.startsWith(p),
  );
  const { data, isLoading, error } = useQuery({
    queryKey: [LOGGED_IN_USER],
    queryFn: () => fetch(PROFILE_DTO),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  React.useEffect(() => {
    if (isLoading || isPublicPath) return;
    // Was: `if (data == null && !error)`. That's ambiguous - fetch()'s generic
    // helper can return `data === null` for reasons that have nothing to do
    // with the session being invalid (an empty-but-successful response body,
    // or previously - before fetch() was fixed to throw instead of silently
    // swallowing failures - any unrelated request error at all), which forced
    // a real logout for e.g. a transient network hiccup. A genuinely expired/
    // failed token refresh already goes through authApi's request interceptor
    // sending a headerless request, which the backend correctly answers with a
    // real 401, already handled by PrivateRoute's response interceptor - this
    // effect only needs to catch the one case that isn't: no token in storage
    // at all (e.g. a protected route hit directly with no session).
    if (!getToken()) {
      logout();
      navigate(urlPaths.LOGIN);
    }
  }, [isLoading, isPublicPath]);

  return { data, isLoading, error };
}

export function useGetUserId() {
  const queryClient = useQueryClient();
  const profile = queryClient.getQueryData([LOGGED_IN_USER]);
  return Number(profile?.id);
}

export default function useUser() {
  const { t } = useTranslation();
  const { data, isLoading, error } = useFetchUser();
  const isBasic = data?.currentClinic?.basicTier || data?.clinic?.basicTier;
  const profile = React.useMemo(
    () => ({
      id: data?.id ?? null,
      birthday: data?.birthday ?? null,
      email: data?.email ?? null,
      firstName: data?.firstName ?? null,
      lastName: data?.lastName ?? null,
      username: data?.username ?? null,
      role: data?.role ?? null,
      phoneNumber: data?.phoneNumber ?? null,
      personToContact: data?.personToContact ?? null,
      oib: data?.oib ?? null,
      initials: data?.firstName
        ? String(data.firstName).charAt(0).toUpperCase() +
          (data.lastName ? String(data.lastName).charAt(0).toUpperCase() : "")
        : "",
      googleid: getGoogleId() ?? null,
      address: data?.address ?? null,
      createdAT: data?.createdAt,
      lastLogin: data?.lastLogin,
    }),
    [data],
  );

  const preferance = React.useMemo(
    () => ({
      defaultLanguage: data?.defaultLanguage ?? null,
      defaultTheme: data?.defaultTheme ?? null,
      defaultCalendarView: data?.defaultCalendarView ?? null,
      appointmentTableRowsPerPage: data?.appointmentTableRowsPerPage ?? 10,
      clinicUsersTableRowsPerPage: data?.clinicUsersTableRowsPerPage ?? 10,
      sendAppointmentEmailsWhenUpdated:
        Boolean(data?.sendAppointmentEmailsWhenUpdated) ?? true,
      sendAppointmentEmails: Boolean(data?.sendAppointmentEmails) ?? true,
      sendInboxEmails: Boolean(data?.sendInboxEmails) ?? true,
      disableAllEmails: Boolean(data?.disableAllEmails) ?? false,
    }),
    [data],
  );

  const location = React.useMemo(
    () => ({
      id: data?.location?.id ?? null,
      country: t(data?.location?.country) ?? null,
      city: data?.location?.city ?? null,
      province: data?.location?.province ?? null,
      place: data?.location?.place ?? null,
      address: data?.location?.address ?? null,
      addressNumber: data?.location?.addressNumber ?? null,
      zipcode: data?.location?.zipcode ?? null,
    }),
    [data, t],
  );

  const currentClinic = React.useMemo(
    () => ({
      id: data?.clinic?.id ?? data?.currentClinic?.id ?? null,
      title: data?.clinic?.title ?? data?.currentClinic?.title ?? null,
      shortName:
        data?.clinic?.shortName ?? data?.currentClinic?.shortName ?? null,
      city: data?.clinic?.city ?? data?.currentClinic?.city ?? null,
      zipCode: data?.clinic?.zipCode ?? data?.currentClinic?.zipCode ?? null,
      country: data?.clinic?.country ?? data?.currentClinic?.country ?? null,
      phoneNumber:
        data?.clinic?.phoneNumber ?? data?.currentClinic?.phoneNumber ?? null,
      email: data?.clinic?.email ?? data?.currentClinic?.email ?? null,
      streetAddress:
        data?.clinic?.streetAddress ??
        data?.currentClinic?.streetAddress ??
        null,
      description:
        data?.clinic?.description ?? data?.currentClinic?.description ?? null,
      url: data?.clinic?.url ?? data?.currentClinic?.url ?? null,
      basicTier: isBasic ?? false,
      aiAssistantEnabled: Boolean(
        data?.clinic?.aiAssistantEnabled ??
          data?.currentClinic?.aiAssistantEnabled,
      ),
    }),
    [data],
  );

  const clinics = React.useMemo(
    () =>
      data?.clinics?.map((clinic) => ({
        id: clinic?.id ?? null,
        title: clinic?.title ?? null,
        shortName: clinic?.shortName ?? null,
        city: clinic?.city ?? null,
        zipCode: clinic?.zipCode ?? null,
        country: clinic?.country ?? null,
        phoneNumber: clinic?.phoneNumber ?? null,
        email: clinic?.email ?? null,
        streetAddress: clinic?.streetAddress ?? null,
        description: clinic?.description ?? null,
        url: clinic?.url ?? null,
      })) ?? [],
    [data],
  );

  const booleans = React.useMemo(
    () => ({
      businessAccount: Boolean(data?.businessAccount) ?? false,
      shelter: Boolean(data?.shelter) ?? false,
      needToChangePassword: Boolean(getNeedToChangePassword()) ?? false,
      subscribeToEmail: Boolean(getSubsribesToEmail()) ?? false,
      reachedPendingLimit: Boolean(data?.reachedPendingLimit) ?? false,
      isPasswordEmpty: Boolean(isPasswordEmpty()) ?? false,
      sendEmailNotifications: Boolean(data?.sendEmailNotifications) ?? true,
    }),
    [data],
  );

  const currentRole = React.useMemo(() => {
    const roleStr = String(data?.role).toUpperCase();
    return Object.values(roleEnums).find((r) => r === roleStr) ?? null;
  }, [data?.role]);

  const roles = React.useMemo(
    () => ({
      master: Boolean(
        String(data?.role).toLocaleUpperCase() === roleEnums.MASTER,
      ),
      admin: Boolean(String(data?.role).toUpperCase() === roleEnums.ADMIN),
      employee: Boolean(
        String(data?.role).toUpperCase() === roleEnums.EMPLOYEE,
      ),
      user: Boolean(String(data?.role).toUpperCase() === roleEnums.USER),
      adminOrEmployee:
        Boolean(String(data?.role).toUpperCase() === roleEnums.ADMIN) ||
        Boolean(String(data?.role).toUpperCase() === roleEnums.EMPLOYEE),
    }),
    [data],
  );

  return {
    roles,
    data,
    isLoading,
    error,
    booleans,
    clinics,
    currentClinic,
    location,
    profile,
    preferance,
    currentRole
  };
}
